/**
 * Suppress console messages in production environment
 * Allows errors and warnings in development, but suppresses dev-only logs in production
 * 
 * IMPORTANT: Preserves original console methods for Sentry breadcrumb capture
 */

const isDev = import.meta.env.DEV;

// Store original console methods BEFORE any suppression
// This allows Sentry's captureConsoleIntegration to capture breadcrumbs
const originalConsole = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
};

// Export original console for Sentry (if needed)
export { originalConsole };

// Suppress findDOMNode deprecation warnings from react-quill in development
// These warnings cause severe performance degradation due to React Strict Mode
if (isDev) {
  const originalWarn = console.warn;
  const originalError = console.error;
  console.warn = function (...args: any[]) {
    const message = args[0]?.toString?.() || '';

    // Suppress findDOMNode deprecation warnings from react-quill
    // These trigger on every render in Strict Mode and cause performance issues
    if (message.includes('findDOMNode') || message.includes('ReactQuill')) {
      return;
    }

    // Suppress unknown prop warnings from MUI
    if (message.includes('disableUnderline') || message.includes('does not recognize')) {
      return;
    }

    originalWarn.apply(console, args);
  };

  // Also suppress in console.error for React warnings
  console.error = function (...args: any[]) {
    const message = args[0]?.toString?.() || '';

    // Suppress React deprecation warnings
    if (message.includes('findDOMNode') || message.includes('ReactQuill')) {
      return;
    }

    originalError.apply(console, args);
  };
}

if (!isDev) {
  // Store wrapped console methods
  const originalWarn = console.warn;
  const originalLog = console.log;

  // Suppress specific production warnings while preserving Sentry capture
  console.warn = function (...args: any[]) {
    const message = args[0]?.toString?.() || '';
    
    // Always call original console for Sentry breadcrumbs first
    originalConsole.warn(...args);
    
    // Suppress React DevTools message
    if (message.includes('React DevTools')) {
      return;
    }
    
    // Suppress React key warnings from Material-UI components
    if (message.includes('key') && message.includes('JSX')) {
      return;
    }
    
    // Suppress unknown prop warnings (like disableUnderline on DOM elements)
    if (message.includes('props object containing a "key" prop')) {
      return;
    }
    
    if (message.includes('does not recognize')) {
      return;
    }

    // Allow other warnings (already sent to Sentry via originalConsole.warn above)
    originalWarn.apply(console, args);
  };

  console.log = function (...args: any[]) {
    const message = args[0]?.toString?.() || '';
    
    // Always call original console for Sentry breadcrumbs first
    originalConsole.log(...args);
    
    // Suppress i18next debug logs
    if (message.includes('i18next')) {
      return;
    }
    
    // Suppress language analytics events
    if (message.includes('Language Analytics Event')) {
      return;
    }
    
    // Suppress analytics tracking logs
    if (message.includes('Analytics Event')) {
      return;
    }

    // Allow other logs (already sent to Sentry via originalConsole.log above)
    originalLog.apply(console, args);
  };
}
